import type { Element, Root, RootContent } from "hast";
import {
  type FC,
  memo,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remend from "remend";
import {
  applyLinkifiers,
  chatbotUrlTransform,
} from "~/pages/appointments/components/chatbotLinks";
import "./ChatMarkdown.css";

// Keep each mounted word's animation stable as later tokens arrive. If markdown
// reparents earlier text (e.g. completing **bold**), it must not fade again.
const StreamingWord: FC<{ initialFade: boolean; children: ReactNode }> = ({
  initialFade,
  children,
}) => {
  const [fade] = useState(initialFade);
  return (
    <span className={fade ? "indy-stream-word" : undefined}>{children}</span>
  );
};

const StreamingSpan: Components["span"] = ({ node, children }) => (
  <StreamingWord
    key={String(node?.properties.indyWordId)}
    initialFade={node?.properties.indyFade === true}
  >
    {children}
  </StreamingWord>
);

function rehypeStreamingWords(
  animate: boolean,
  seenLength: number,
  seenNodes: Map<number, number>,
  nextNodes: Map<number, number>,
  pendingDelimiter: { start: number; length: number } | undefined,
) {
  return () => (tree: Root) => {
    const walk = (parent: Root | Element) => {
      parent.children = parent.children.flatMap((node): RootContent[] => {
        if (node.type === "element") {
          walk(node);
          return [node];
        }
        if (node.type !== "text") return [node];
        // A delimiter-only tail has no content to format yet. Hide it until
        // the next token clarifies it; literal markers inside code survive.
        const value =
          pendingDelimiter &&
          node.position?.end.offset ===
            pendingDelimiter.start + pendingDelimiter.length &&
          !(parent.type === "element" && parent.tagName === "code")
            ? node.value.slice(0, -pendingDelimiter.length)
            : node.value;
        node.value = value;
        if (!animate) return [node];
        const start = node.position?.start.offset;
        // Structural whitespace (between table rows, for example) has no
        // source position and must remain text to preserve valid HTML.
        if (start === undefined || !node.value.trim()) return [node];
        // Rendered lengths account for entities and escaped markdown. Source
        // offsets are the fallback when formatting creates a new text node.
        const seen = seenNodes.get(start) ?? Math.max(0, seenLength - start);
        nextNodes.set(start, node.value.length);
        return Array.from(
          node.value.matchAll(/\s+|\S+/g),
          (match): RootContent => {
            if (!match[0].trim()) return { type: "text", value: match[0] };
            return {
              type: "element",
              tagName: "span",
              properties: {
                // react-markdown keys sibling spans by their ordinal. A
                // nested key keeps animation state tied to the source word
                // when formatting moves other words under a new element.
                indyWordId: `${start}:${match.index}`,
                indyFade: match.index + match[0].length > seen,
              },
              children: [{ type: "text", value: match[0] }],
            };
          },
        );
      });
    };
    walk(tree);
  };
}

// Completed prose must not be reparsed on every token in a different reply.
export const ChatMarkdown: FC<{
  text: string;
  components: Components;
  streaming?: boolean;
  stopped?: boolean;
}> = memo(({ text, components, streaming = false, stopped = false }) => {
  const linkedMarkdown = applyLinkifiers(text);
  const incomplete = streaming || stopped;
  const markdown = incomplete
    ? remend(linkedMarkdown, {
        // Complete inline formatting only. Existing links, images, HTML,
        // math and block parsing keep their established behavior.
        links: false,
        images: false,
        htmlTags: false,
        katex: false,
        inlineKatex: false,
        comparisonOperators: false,
        singleTilde: false,
        setextHeadings: false,
      })
    : linkedMarkdown;
  // Defer a delimiter-only token while streaming, never a word's literal
  // suffix. Once stopped, show the tail even if no formatting content arrived.
  const tail = streaming ? /(?:^|\s)([*_~]+)$/.exec(markdown) : null;
  const pendingDelimiter = tail
    ? { start: markdown.length - tail[1].length, length: tail[1].length }
    : undefined;
  // Retain the same tree through completion/stop; history mounts without spans.
  const [animate] = useState(streaming);
  const seenLength = useRef(0);
  const seenNodes = useRef(new Map<number, number>());
  const nextNodes = new Map<number, number>();
  useLayoutEffect(() => {
    // Remend's temporary closing markers are not received source text.
    seenLength.current = linkedMarkdown.length;
    seenNodes.current = nextNodes;
  });

  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={
        animate || incomplete
          ? [
              rehypeStreamingWords(
                animate,
                seenLength.current,
                seenNodes.current,
                nextNodes,
                pendingDelimiter,
              ),
            ]
          : []
      }
      urlTransform={chatbotUrlTransform}
      components={animate ? { ...components, span: StreamingSpan } : components}
    >
      {markdown}
    </Markdown>
  );
});
